"use client";

import { CalendarDays, PenTool } from "lucide-react";
import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import { primaryButtonClass } from "@/components/form";
import type { EditorStyle } from "@/components/pdf-editor/EditorStage";
import { EditorWorkspace } from "@/components/pdf-editor/EditorWorkspace";
import { ProgressBar } from "@/components/ProgressBar";
import { blobToDataUrl, dataUrlToBlob, type Signature } from "@/components/sign/signature-canvas";
import { SignatureMaker } from "@/components/sign/SignatureMaker";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { useEditor } from "@/hooks/useEditor";
import { useToolFiles } from "@/hooks/useToolFiles";
import { outputName } from "@/lib/files";
import { readImageSize } from "@/lib/image/info";
import { forgetSignature, recallSignature, rememberSignature } from "@/lib/sign/signature";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
import { annotateFile } from "@/workers/pdf-client";
import { shownPageSize } from "@/workers/pdf-render";
const tool = getTool("sign-pdf")!;
const STYLE: EditorStyle = {
  color: "#0f1222",
  strokeWidth: 2,
  highlightColor: "#ffd500",
  highlightWidth: 14,
  font: "sans",
  fontSize: 12,
  fill: null
};
const storage = () => typeof window === "undefined" ? null : window.localStorage;

/** A remembered signature, read once when the page opens (never sent anywhere). */
function savedSignature(): Signature | null {
  const store = storage();
  const url = store && recallSignature(store);
  if (!url) return null;
  const blob = dataUrlToBlob(url);
  const bytes = Uint8Array.from(atob(url.split(",")[1].slice(0, 400)), c => c.charCodeAt(0));
  const size = readImageSize(bytes) ?? {
    width: 300,
    height: 100
  };
  return {
    blob,
    ...size
  };
}

/** FR-14: draw, type or upload a signature; place and resize it on any page. */
export function SignTool() {
  const {
    files,
    setFiles,
    notices
  } = useToolFiles(tool);
  const file = files[0];
  const editor = useEditor();
  const [page, setPage] = useState(0);
  const [signature, setSignature] = useState<Signature | null>(savedSignature);
  const [making, setMaking] = useState(() => !savedSignature());
  const [remember, setRemember] = useState(() => savedSignature() !== null);
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function store(sig: Signature | null, on: boolean) {
    const s = storage();
    if (!s) return;
    if (!on || !sig) {
      forgetSignature(s);
      return;
    }
    if (!rememberSignature(s, await blobToDataUrl(sig.blob))) {
      setError("This signature couldn't be remembered on this device (it may be too large).");
    }
  }
  async function place() {
    if (!file || !signature) return;
    const size = await shownPageSize(file, page);
    const width = Math.min(size.width * 0.35, 180);
    const height = width * signature.height / signature.width;
    editor.add({
      page,
      kind: "image",
      format: "png",
      blob: signature.blob,
      x: size.width * 0.55 - width / 2,
      y: size.height * 0.8 - height / 2,
      width,
      height
    });
  }
  async function addDate() {
    if (!file) return;
    const size_0 = await shownPageSize(file, page);
    const today = new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
    editor.add({
      page,
      kind: "text",
      x: size_0.width * 0.55 - 40,
      y: size_0.height * 0.88,
      text: today,
      font: "sans",
      size: 12,
      color: "#0f1222"
    });
  }
  async function save() {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const blob = await annotateFile(file, editor.items);
      setResults([{
        blob,
        name: outputName(file.name, "signed", "pdf")
      }]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function restart() {
    editor.reset();
    setPage(0);
    setResults(null);
    setError("");
  }
  const signatures = editor.items.filter(i => i.kind === "image").length;
  const toolButton = "inline-flex min-h-11 items-center gap-1.5 rounded-lg border-[1.5px] border-line-strong bg-card px-3 text-sm font-medium hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card";
  return <ToolShell tool={tool} files={files} onFilesChange={next => {
    setFiles(next);
    restart();
  }} notices={notices} results={results} onStartOver={() => {
    setFiles([]);
    restart();
  }}>
      {file && <EditorWorkspace file={file} editor={editor} page={page} onPageChange={p => {
      setPage(p);
      editor.select(null);
    }} mode="select" style={STYLE} toolbar={<div role="toolbar" aria-label="Add to the page" className="flex flex-wrap gap-1.5">
              <button type="button" className={toolButton} onClick={place} disabled={!signature || making}>
                <PenTool className="size-4" aria-hidden="true" />
                Place signature on page {page + 1}
              </button>
              <button type="button" className={toolButton} onClick={addDate}>
                <CalendarDays className="size-4" aria-hidden="true" />
                Add today&apos;s date
              </button>
            </div>} panel={making || !signature ? <SignatureMaker onDone={sig_0 => {
      setSignature(sig_0);
      setMaking(false);
      if (remember) store(sig_0, true);
    }} /> : <div className="flex flex-col gap-3">
                <h2 className="text-lg">Your signature</h2>
                <div className="flex justify-center rounded-lg border bg-white p-3">
                  <BlobImage blob={signature.blob} alt="Your signature" className="max-h-20 max-w-full" />
                </div>
                <button type="button" className={cn(primaryButtonClass, "min-h-11 text-base")} onClick={place}>
                  Place on page {page + 1}
                </button>
                <button type="button" className="min-h-11 rounded-lg border-[1.5px] border-ink px-4 font-semibold hover:bg-sand" onClick={() => setMaking(true)}>
                  Make a new signature
                </button>
                <p className="text-[13px] text-muted-foreground">
                  Drag it into place and use the corners to resize. You can
                  place it on several pages.
                </p>
              </div>} footer={<section aria-label="Save" className="z-20 flex flex-col gap-3 rounded-[16px] border bg-card p-4 sm:sticky sm:bottom-0 sm:flex-row sm:items-center sm:justify-between sm:shadow-[0_-4px_12px_rgba(31,36,48,0.06)]">
              <div className="flex flex-col gap-1 text-sm">
                <label className="flex min-h-11 items-center gap-3 font-semibold">
                  <input type="checkbox" className="size-5 accent-brand" checked={remember} onChange={e_0 => {
            setRemember(e_0.target.checked);
            store(signature, e_0.target.checked);
          }} />
                  Remember my signature on this device
                </label>
                <span className="text-muted-foreground">
                  {remember ? "Saved only in this browser. Untick to forget it." : "Off: your signature is forgotten when you close this page."}{" "}
                  Nothing is uploaded.
                </span>
                {error && <span role="alert" className="font-medium text-destructive">
                    {error}
                  </span>}
              </div>
              {busy ? <div className="sm:w-64">
                  <ProgressBar label="Saving your signed PDF…" />
                </div> : <button type="button" onClick={save} disabled={signatures === 0} className={cn(primaryButtonClass, "sm:w-auto")}>
                  Save signed PDF
                </button>}
            </section>} />}
    </ToolShell>;
}